import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
    LayoutDashboard,
    Package,
    Receipt,
    TrendingUp,
    Warehouse,
    Undo2,
    Coins,
    Settings as SettingsIcon,
    LogOut,
    RefreshCw,
    Users as UsersIcon,
    Megaphone,
    Menu,
    X,
    ChevronLeft,
    ChevronRight,
    FileText,
} from "lucide-react";
import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";

const NAV = [
    { to: "/", label: "Overview", icon: LayoutDashboard, testid: "nav-overview" },
    { to: "/sales", label: "Sales", icon: TrendingUp, testid: "nav-sales" },
    { to: "/products", label: "Products", icon: Package, testid: "nav-products" },
    { to: "/inventory", label: "Inventory", icon: Warehouse, testid: "nav-inventory" },
    { to: "/transactions", label: "Transactions", icon: Receipt, testid: "nav-transactions" },
    { to: "/returns", label: "Returns", icon: Undo2, testid: "nav-returns" },
    { to: "/profitability", label: "Profitability", icon: Coins, testid: "nav-profitability" },
    { to: "/marketing", label: "Marketing", icon: Megaphone, testid: "nav-marketing" },
    { to: "/stock-recon", label: "Stock Recon", icon: FileText, testid: "nav-stock-recon" },
];

const ADMIN_NAV = [
    { to: "/users", label: "Users", icon: UsersIcon, testid: "nav-users" },
    { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

const COLLAPSE_KEY = "ti.sidebar.collapsed";

export default function Layout() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [syncing, setSyncing] = useState(false);
    const [jobStatus, setJobStatus] = useState(null);
    const [collapsed, setCollapsed] = useState(() => {
        try {
            return localStorage.getItem(COLLAPSE_KEY) === "1";
        } catch {
            return false;
        }
    });
    const [mobileOpen, setMobileOpen] = useState(false);

    const refreshJob = async () => {
        try {
            const { data } = await api.get("/takealot/status");
            setJobStatus(data.job);
        } catch {
            // silent — status polling is best-effort
        }
    };

    useEffect(() => {
        refreshJob();
        const i = setInterval(refreshJob, 5000);
        return () => clearInterval(i);
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
        } catch {
            // localStorage may be blocked
        }
    }, [collapsed]);

    // Close mobile drawer when window grows past md
    useEffect(() => {
        const onResize = () => {
            if (window.innerWidth >= 768) setMobileOpen(false);
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const onSync = async () => {
        setSyncing(true);
        try {
            const { data } = await api.post("/takealot/sync?all_time=true");
            if (data.started) toast.success("Sync started — pulling full history. This may take 30-90s.");
            else toast.info(data.reason || "Sync already running");
            refreshJob();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSyncing(false);
        }
    };

    const isAdmin = user?.role === "admin";

    const renderNavItem = ({ to, label, icon: Icon, testid, accentActive = "var(--bl-takealot-blue)" }) => (
        <NavLink
            key={to}
            to={to}
            end={to === "/"}
            data-testid={testid}
            onClick={() => setMobileOpen(false)}
            title={collapsed ? label : undefined}
            className={({ isActive }) =>
                `flex items-center gap-3 ${collapsed ? "justify-center px-2" : "px-3"} py-2 text-sm transition-colors rounded-sm border-l-2 ${
                    isActive
                        ? "bg-[color:var(--bl-surface-2)] text-[color:var(--bl-text)] font-medium"
                        : "border-transparent text-[color:var(--bl-text-dim)] hover:text-[color:var(--bl-text)] hover:bg-[color:var(--bl-surface-2)]/60"
                }`
            }
            style={({ isActive }) =>
                isActive ? { borderLeftColor: accentActive } : undefined
            }
        >
            <Icon className="w-4 h-4 shrink-0" />
            {!collapsed && <span className="truncate">{label}</span>}
        </NavLink>
    );

    const sidebarWidthClass = collapsed ? "md:w-16" : "md:w-64";

    return (
        <div className="grain min-h-screen bg-[color:var(--bl-bg)] text-[color:var(--bl-text)] relative">
            <Toaster position="top-right" theme="light" richColors />

            {/* Mobile top bar */}
            <header className="md:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 border-b border-[color:var(--bl-border)] bg-[color:var(--bl-surface)]">
                <button
                    data-testid="mobile-menu-toggle"
                    onClick={() => setMobileOpen(true)}
                    className="p-1.5 -ml-1 text-[color:var(--bl-text)] hover:text-[color:var(--bl-takealot-orange)]"
                    aria-label="Open menu"
                >
                    <Menu className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-2 min-w-0">
                    <img src="/bull.png" alt="" className="w-8 h-8 object-contain rounded-full" />
                    <div className="font-serif-display text-lg leading-none truncate">
                        Takealot Insight
                    </div>
                </div>
                <button
                    data-testid="mobile-sync-btn"
                    onClick={onSync}
                    disabled={syncing || jobStatus?.status === "in_progress"}
                    className="p-1.5 text-[color:var(--bl-takealot-blue)] hover:brightness-110 disabled:opacity-50"
                    aria-label="Sync"
                    title="Sync Takealot"
                >
                    <RefreshCw
                        className={`w-5 h-5 ${syncing || jobStatus?.status === "in_progress" ? "animate-spin" : ""}`}
                    />
                </button>
            </header>

            <div className="flex">
                {/* Mobile backdrop */}
                {mobileOpen && (
                    <div
                        data-testid="mobile-menu-backdrop"
                        className="md:hidden fixed inset-0 z-40 bg-black/40"
                        onClick={() => setMobileOpen(false)}
                    />
                )}

                {/* Sidebar */}
                <aside
                    data-testid="sidebar"
                    className={`${sidebarWidthClass} ${
                        mobileOpen ? "translate-x-0" : "-translate-x-full"
                    } md:translate-x-0 fixed md:sticky top-0 md:top-0 left-0 z-50 md:z-10 h-screen md:h-screen w-64 shrink-0 border-r border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] flex flex-col transition-[transform,width] duration-200 ease-out`}
                >
                    {/* Brand */}
                    <div
                        className={`px-4 md:px-4 py-4 border-b border-[color:var(--bl-border)] flex items-center ${
                            collapsed ? "md:justify-center" : "justify-between"
                        } gap-3`}
                    >
                        <div className={`flex items-center gap-3 min-w-0 ${collapsed ? "md:justify-center" : ""}`}>
                            <img
                                src="/bull.png"
                                alt=""
                                className="w-10 h-10 object-contain rounded-full shrink-0"
                                data-testid="brand-logo"
                            />
                            {!collapsed && (
                                <div className="min-w-0">
                                    <div className="font-serif-display text-lg text-[color:var(--bl-text)] leading-none truncate">
                                        Takealot Insight
                                    </div>
                                    <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--bl-takealot-orange)] mt-1.5 font-medium truncate">
                                        Brothers Coffee
                                    </div>
                                </div>
                            )}
                        </div>
                        {/* Mobile close */}
                        <button
                            data-testid="mobile-menu-close"
                            onClick={() => setMobileOpen(false)}
                            className="md:hidden text-[color:var(--bl-text-dim)] hover:text-[color:var(--bl-text)] p-1"
                            aria-label="Close menu"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Nav */}
                    <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5">
                        {NAV.map((n) => renderNavItem(n))}
                        {isAdmin && (
                            <>
                                {!collapsed && (
                                    <div className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-[0.2em] text-[color:var(--bl-text-mute)]">
                                        Admin
                                    </div>
                                )}
                                {collapsed && (
                                    <div className="my-2 border-t border-[color:var(--bl-border)]" />
                                )}
                                {ADMIN_NAV.map((n) =>
                                    renderNavItem({ ...n, accentActive: "var(--bl-takealot-orange)" }),
                                )}
                            </>
                        )}
                    </nav>

                    {/* Footer */}
                    <div className="px-2 py-3 border-t border-[color:var(--bl-border)] space-y-2">
                        <button
                            data-testid="sync-takealot-btn"
                            onClick={onSync}
                            disabled={syncing || jobStatus?.status === "in_progress"}
                            title={collapsed ? "Sync Takealot" : undefined}
                            className={`w-full flex items-center justify-center gap-2 text-xs uppercase tracking-wider py-2 bg-[color:var(--bl-takealot-blue)] text-white hover:brightness-110 transition disabled:opacity-50 rounded-sm ${
                                collapsed ? "px-0" : "px-2"
                            }`}
                        >
                            <RefreshCw
                                className={`w-3.5 h-3.5 shrink-0 ${syncing || jobStatus?.status === "in_progress" ? "animate-spin" : ""}`}
                            />
                            {!collapsed && (
                                <span className="truncate">
                                    {jobStatus?.status === "in_progress" ? "Syncing…" : "Sync Takealot"}
                                </span>
                            )}
                        </button>
                        {!collapsed && jobStatus?.status === "in_progress" && (
                            <div
                                className="text-[10px] text-[color:var(--bl-text-dim)] text-center"
                                data-testid="sync-progress-text"
                            >
                                Background sync running
                            </div>
                        )}

                        {!collapsed ? (
                            <div className="flex items-center justify-between px-2 py-2 text-xs text-[color:var(--bl-text-dim)] gap-2">
                                <div className="truncate min-w-0" data-testid="user-email">
                                    {user?.email}
                                </div>
                                <button
                                    data-testid="logout-button"
                                    onClick={async () => {
                                        await logout();
                                        nav("/login");
                                    }}
                                    className="text-[color:var(--bl-text-dim)] hover:text-[color:var(--bl-takealot-orange)] shrink-0"
                                    title="Sign out"
                                >
                                    <LogOut className="w-4 h-4" />
                                </button>
                            </div>
                        ) : (
                            <button
                                data-testid="logout-button"
                                onClick={async () => {
                                    await logout();
                                    nav("/login");
                                }}
                                className="w-full flex justify-center py-2 text-[color:var(--bl-text-dim)] hover:text-[color:var(--bl-takealot-orange)]"
                                title="Sign out"
                            >
                                <LogOut className="w-4 h-4" />
                            </button>
                        )}

                        {/* Desktop collapse toggle */}
                        <button
                            data-testid="sidebar-collapse-toggle"
                            onClick={() => setCollapsed((c) => !c)}
                            className="hidden md:flex w-full items-center justify-center gap-1.5 py-1.5 text-[10px] uppercase tracking-wider text-[color:var(--bl-text-dim)] hover:text-[color:var(--bl-text)] border border-[color:var(--bl-border)] rounded-sm hover:bg-[color:var(--bl-surface-2)]/60"
                            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                        >
                            {collapsed ? (
                                <ChevronRight className="w-3.5 h-3.5" />
                            ) : (
                                <>
                                    <ChevronLeft className="w-3.5 h-3.5" />
                                    <span>Collapse</span>
                                </>
                            )}
                        </button>
                    </div>
                </aside>

                <main className="flex-1 min-w-0 overflow-x-hidden relative z-10">
                    <div className="px-4 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-8 max-w-[1400px] mx-auto fade-in-up">
                        <Outlet />
                    </div>
                </main>
            </div>
        </div>
    );
}
